import { useEffect, useRef, useState } from "react";
import { stripNullBytes } from "../../shared/stripNullBytes";
import { executionContentPath } from "../lib/apiPaths";
import { isInlineTextContentType } from "../lib/inlineContentType";

const MAX_INLINE_BYTES = 5 * 1024 * 1024;
const PREVIEW_BYTES = 64 * 1024;

type ContentResult = {
  content: string | undefined;
  contentType: string | undefined;
  size: number | undefined;
  truncated: boolean;
};

const empty: ContentResult = {
  content: undefined,
  contentType: undefined,
  size: undefined,
  truncated: false,
};

async function readPreview(body: ReadableStream<Uint8Array>): Promise<string> {
  const reader = body.getReader();
  const buffers: ArrayBuffer[] = [];
  let bytesRead = 0;
  try {
    while (bytesRead < PREVIEW_BYTES) {
      const { done, value } = await reader.read();
      if (done) break;
      buffers.push(new Uint8Array(value).buffer as ArrayBuffer);
      bytesRead += value.byteLength;
    }
  } finally {
    reader.cancel();
  }
  return new Blob(buffers).slice(0, PREVIEW_BYTES).text();
}

export function useExecutionContent(
  execution:
    | { id: string; status: "pending" | "success" | "error" }
    | undefined,
  field: "input" | "output" | "log" | "requests",
  enabled = true
) {
  const [result, setResult] = useState<ContentResult>(empty);
  const [settledFetch, setSettledFetch] = useState<string>();
  // When a log finishes streaming, the deps below flip and the effect re-runs.
  // Without this, we'd briefly setResult(empty) and unmount the CodeEditor,
  // dropping the user's scroll position. Track the last context that was
  // streaming so we can skip the blank on that exact transition.
  const lastStreamingContextRef = useRef<string | undefined>(undefined);

  const executionId = execution?.id;
  const status = execution?.status;
  const isPendingTail =
    (field === "log" || field === "requests") && status === "pending";
  const shouldFetch =
    field === "input" ||
    field === "log" ||
    field === "requests" ||
    (field === "output" && status === "success");
  const context = `${executionId ?? ""}:${field}`;
  const fetching =
    executionId !== undefined && enabled && shouldFetch && !isPendingTail;
  const loading = fetching && settledFetch !== context;

  useEffect(() => {
    if (!executionId || !enabled || !shouldFetch) {
      // oxlint-disable-next-line react/react-compiler -- EffectSetState: resets to empty at the top of the AbortController execution-content fetch
      setResult(empty);
      setSettledFetch(undefined);
      lastStreamingContextRef.current = undefined;
      return;
    }

    const controller = new AbortController();
    const { signal } = controller;

    if (isPendingTail) {
      setResult(empty);
      lastStreamingContextRef.current = context;
      void (async () => {
        // The tail endpoint either returns a single snapshot and closes
        // (filesystem mode) or streams progressively until the execution
        // finishes (blobstore mode), so we re-poll once a second after each
        // clean close. NUL bytes are server-side keepalives, strip them
        // before rendering. We only enlarge `displayed` so re-polled
        // snapshots don't briefly flash empty.
        let displayed = "";
        while (!signal.aborted) {
          try {
            const res = await fetch(
              executionContentPath(executionId, field, { tail: true }),
              { credentials: "include", signal }
            );
            if (!res.ok || !res.body) {
              await new Promise((r) => setTimeout(r, 1000));
              continue;
            }
            const reader = res.body.getReader();
            const decoder = new TextDecoder();
            let snapshot = "";
            while (!signal.aborted) {
              const { done, value } = await reader.read();
              if (done) break;
              snapshot += stripNullBytes(
                decoder.decode(value, { stream: true })
              );
              if (snapshot.length > displayed.length) {
                displayed = snapshot;
                setResult({
                  content: displayed,
                  contentType: "text/plain",
                  size: displayed.length,
                  truncated: false,
                });
              }
            }
            snapshot += stripNullBytes(decoder.decode());
            if (!signal.aborted && snapshot.length > displayed.length) {
              displayed = snapshot;
              setResult({
                content: displayed,
                contentType: "text/plain",
                size: displayed.length,
                truncated: false,
              });
            }
            if (signal.aborted) return;
            await new Promise((r) => setTimeout(r, 1000));
          } catch (err) {
            if (err instanceof Error && err.name === "AbortError") return;
            await new Promise((r) => setTimeout(r, 500));
          }
        }
      })();
      return () => controller.abort();
    }

    const wasStreaming = lastStreamingContextRef.current === context;
    lastStreamingContextRef.current = undefined;
    if (!wasStreaming) setResult(empty);
    void fetch(executionContentPath(executionId, field), {
      credentials: "include",
      signal,
    })
      .then(async (res) => {
        if (!res.ok) {
          setResult(empty);
          setSettledFetch(context);
          return;
        }
        const contentType =
          res.headers.get("Content-Type")?.split(";")[0] ?? "text/plain";
        const headerSize = Number(res.headers.get("Content-Length")) || 0;

        const needsContent = isInlineTextContentType(contentType);

        if (!needsContent) {
          res.body?.cancel();
          if (!signal.aborted) {
            setResult({
              content: undefined,
              contentType,
              size: headerSize,
              truncated: false,
            });
            setSettledFetch(context);
          }
          return;
        }

        if (headerSize > MAX_INLINE_BYTES) {
          const content = res.body ? await readPreview(res.body) : undefined;
          if (!signal.aborted) {
            setResult({
              content,
              contentType,
              size: headerSize,
              truncated: true,
            });
            setSettledFetch(context);
          }
          return;
        }

        const blob = await res.blob();
        if (signal.aborted) return;

        if (blob.size > MAX_INLINE_BYTES) {
          const content = await blob.slice(0, PREVIEW_BYTES).text();
          setResult({
            content,
            contentType,
            size: blob.size,
            truncated: true,
          });
          setSettledFetch(context);
          return;
        }

        const content = await blob.text();
        if (!signal.aborted) {
          setResult({
            content,
            contentType,
            size: blob.size,
            truncated: false,
          });
          setSettledFetch(context);
        }
      })
      .catch(() => {});

    return () => controller.abort();
  }, [executionId, shouldFetch, isPendingTail, field, enabled, context]);

  return { loading, ...result };
}
